import type { Theme } from '@mui/material';
import { alpha } from '@mui/material';
import { IconColors } from 'src/components/icons/icon-utils';

export const EDITOR_PICKER_HEIGHT = 320;

/**
 * Generates shared popover styles for editor-related popovers.
 * These styles provide consistent appearance matching the editor container.
 *
 * @param theme - MUI Theme object
 * @returns Object containing popover style definitions
 */
export function getEditorPopoverStyles(theme: Theme) {
  return {
    /**
     * The title row. The panel's own padding leaves the title tight against its rounded
     * corner, so the row adds its own.
     */
    popoverHeader: {
      padding: theme.spacing(1, 1, 0),
    },
    popoverPaper: {
      border: `1px solid ${theme.appColors.rule}`,
      borderRadius: 20,
      boxShadow: theme.shadows[4],
      padding: 0,
    },
  };
}

/**
 * Generates shared search input styles for editor popovers.
 * These styles provide consistent appearance for search inputs across all editor popovers.
 *
 * @param theme - MUI Theme object
 * @returns Object containing search wrapper and container style definitions
 */
export function getEditorSearchStyles(theme: Theme) {
  return {
    searchInput: {
      backgroundColor: theme.appColors.background2,
      borderRadius: 12,
    },
  };
}

/**
 * Generates shared chip styles for mention and link elements.
 * These styles provide consistent appearance across all chip-based elements in the editor.
 *
 * @param theme - MUI Theme object
 * @returns Object containing chip style definitions
 */
export function getEditorChipStyles(theme: Theme) {
  return {
    /**
     * Slate puts an absolutely positioned, zero-height spacer inside a void chip. In a flex
     * container its position follows `align-self`, so with the centered items it started
     * halfway down the chip and its selection highlight hung below the border.
     */
    '& > [data-slate-spacer]': {
      alignSelf: 'flex-start',
    },
    '&:hover': {
      border: `1px solid ${theme.appColors.primary}`,
    },
    /**
     * While editing, a selection that covers a chip shows as the chip's selected outline
     * rather than as highlighted label text. Read-only text keeps the highlight, so someone
     * copying a message still sees what they are copying.
     */
    '[contenteditable="true"] & ::selection, [contenteditable="true"] &::selection': {
      backgroundColor: 'transparent',
    },
    alignItems: 'center',
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 4,
    boxSizing: 'border-box',
    color: theme.palette.primary.main,
    cursor: 'pointer',
    display: 'inline-flex',
    fontSize: theme.typography.caption.fontSize,
    fontWeight: 600,
    gap: theme.spacing(0.5),
    height: 22,
    margin: theme.spacing(0, 0.25),
    maxWidth: 300,
    padding: theme.spacing(0.25),
    verticalAlign: 'middle',
  } as const;
}

/**
 * Generates shared remove button styles for chip elements (mentions, links).
 * Used for the small X button that removes/deletes the chip element.
 *
 * @returns Object containing remove button style definition
 */
export function getChipRemoveButtonStyles() {
  return {
    chipRemoveButton: {
      '&:hover': {
        backgroundColor: alpha(IconColors.DarkGrey, 0.75),
      },
      alignItems: 'center',
      backgroundColor: alpha(IconColors.DarkGrey, 0.5),
      borderRadius: '50%',
      cursor: 'pointer',
      display: 'flex',
      justifyContent: 'center',
      padding: '2px',
    },
  };
}

/**
 * Generates shared floating toolbar styles for use in makeStyles hooks.
 * These styles provide consistent appearance across all floating toolbars in the editor.
 *
 * @param theme - MUI Theme object
 * @returns Object containing floating toolbar style definitions
 */
export function getFloatingToolbarStyles(theme: Theme) {
  return {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    boxShadow: theme.shadows[4],
    outline: 'none',
    padding: theme.spacing(0.5),
    zIndex: theme.zIndex.tooltip,
  } as const;
}

/**
 * Generates shared toolbar button styles for use in makeStyles hooks.
 * These styles provide consistent appearance across all editor toolbar buttons.
 *
 * @param theme - MUI Theme object
 * @returns Object containing toolbar button style definitions
 */
export function getToolbarButtonStyles(theme: Theme) {
  return {
    toolbarButton: {
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
      borderRadius: 3,
    },
    toolbarButtonDefault: {
      color: theme.palette.text.secondary,
    },
    toolbarButtonPressed: {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.primary.main,
    },
  };
}

/** Shared shell for the full formatting toolbar, opened by selection or the arrow. */
export function getFormatToolbarStyles(theme: Theme) {
  return {
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 10,
    boxShadow: theme.shadows[3],
    maxWidth: 'calc(100vw - 24px)',
    width: 'max-content',
    zIndex: theme.zIndex.tooltip,
  };
}
